export const WORKFORCE_PERMISSIONS = [
  'manage_workforce_shifts',
  'manage_warehouse_operators',
  'mark_attendance',
  'manage_operator_rates',
  'index_operator_costs',
]

export const hasAnyWorkforcePermission = user => {
  if (!user?.permissions) return false
  return WORKFORCE_PERMISSIONS.some(key => user.permissions[key])
}

export const extractBackendError = err => {
  const body = err?.response?.data
  const empty = { fields: {}, base: [], message: 'Unknown error' }
  if (!body || typeof body !== 'object') return empty

  const fields = {}
  const base = []
  if (Array.isArray(body.errors)) {
    for (const entry of body.errors) {
      if (!entry) continue
      const { field, message } = entry
      if (!message) continue
      if (field === 'base') base.push(message)
      else if (field) fields[field] = message
    }
  }

  const e = body.error
  let message = 'Unknown error'
  if (typeof e === 'string') message = e
  else if (e?.message) message = e.message
  else if (e?.param) message = `Invalid parameter: ${e.param}`

  return { fields, base, message }
}

// One row of a period's roster (GET .../shift-schedules/:id/operators). A default operator
// must read as one: removing it is an `included: false` override, while removing an added
// operator just drops its inclusion. A platform that omits `is_default` still sends each
// operator's `default_work_shift_id`, which decides it against the period's shift.
export const toRosterEntry = (op, workShiftId) => {
  const uwId = op.user_warehouse_id ?? op.id
  return {
    user_warehouse_id: uwId,
    full_name: op.user_name || op.user?.full_name || op.full_name,
    operator_type: op.operator_type,
    contract_type: op.contract_type,
    isDefault: op.is_default ?? (workShiftId != null && op.default_work_shift_id === workShiftId),
    included: op.included !== false,
    assignmentEndDate: op.assignment_end_date ?? null,
  }
}

// The decline codes the operators API answers with (`{ error, code }`, 422); each has a message
// under workforce.operators.errors.codes. Any other code falls back to the backend's text.
export const OPERATOR_DECLINE_CODES = Object.freeze([
  'FUTURE_DATE',
  'SHIFT_NOT_IN_WAREHOUSE',
  'NO_RATE',
  'BEFORE_LATEST_ASSIGNMENT',
  'BEFORE_TERMINATION',
  'NOT_ACTIVE',
  'BEFORE_START',
  'ATTENDANCE_AFTER_END_DATE',
  'ASSIGNMENT_CHANGED',
  'TERMINATED',
  'WAREHOUSE_NOT_MEMBER',
])

export const operatorErrorMessage = (err, t) => {
  const code = err?.response?.data?.code
  if (OPERATOR_DECLINE_CODES.includes(code)) return t(`workforce.operators.errors.codes.${code}`)
  return extractBackendError(err).message
}

const assignmentChanged = (operator, form) =>
  form.operatorType !== operator.operator_type || form.contractType !== operator.contract_type || Number(form.workShiftId) !== operator.work_shift_id

// The day after a 'YYYY-MM-DD' date, as 'YYYY-MM-DD' (UTC arithmetic, so no timezone shift).
const dayAfter = iso => {
  const [year, month, day] = iso.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10)
}

// The start a correction moves earlier, and how far it may go: the current (latest) assignment
// from the operator's history (newest first), bounded by the end of the one before it. The
// serializer's start_date is the operator's first day ever, which is a different date as soon as
// they have changed shift or been rehired.
const startCorrection = history => {
  const [current, previous] = Array.isArray(history) ? history : []
  if (!current?.start_date || current.end_date) return null
  return { start: current.start_date, min: previous?.end_date ? dayAfter(previous.end_date) : null }
}

// Which date the operator form asks for. The backend dates every assignment: a hire from its
// start, a change or a rehire from when it applies, a termination on the last day worked. A save
// that changes nothing only ever moves the current assignment's start earlier (a hire recorded
// after their first day); a later date would be ignored, so that start is also the latest one
// allowed. An inactive operator's fields stay locked until they are reactivated: the backend
// refuses to change a terminated assignment.
export const dateFieldFor = ({ editing, operator, form, today, history }) => {
  const asOfToday = field => ({ field, fieldsLocked: false, defaultDate: today, max: today, min: null })
  if (!editing) return asOfToday('start_date')
  if (operator.operator_active && !form.isActive) return asOfToday('end_date')
  if (!operator.operator_active)
    return form.isActive ? asOfToday('effective_date') : { field: null, fieldsLocked: true, defaultDate: null, max: null, min: null }
  if (assignmentChanged(operator, form)) return asOfToday('effective_date')
  const correction = startCorrection(history)
  if (!correction) return { field: null, fieldsLocked: false, defaultDate: null, max: null, min: null }
  return { field: 'start_date', fieldsLocked: false, defaultDate: correction.start, max: correction.start, min: correction.min }
}

// The POST/PUT body for the operator form. The PUT keeps sending the four fields every time; a
// date goes along only when it means something, so an unchanged save writes nothing.
export const buildOperatorPayload = ({ editing, operator, form, userId, history }) => {
  const payload = {
    operator_type: form.operatorType,
    contract_type: form.contractType,
    work_shift_id: Number(form.workShiftId),
    operator_active: editing ? form.isActive : true,
  }
  if (!editing) return { ...payload, user_id: userId, effective_date: form.date }
  if (operator.operator_active && !form.isActive) return { ...payload, end_date: form.date }
  if (!form.isActive) return payload
  if (!operator.operator_active || assignmentChanged(operator, form)) return { ...payload, effective_date: form.date }
  const correction = startCorrection(history)
  if (correction && form.date && form.date < correction.start) return { ...payload, effective_date: form.date }
  return payload
}

// 'YYYY-MM-DD' -> 'dd/mm/yyyy' by slicing: `new Date('2026-10-05')` is UTC midnight, which
// renders as the 4th in Mexico and Brazil.
export const formatIsoDate = iso => {
  if (!iso) return '—'
  const [year, month, day] = String(iso).slice(0, 10).split('-')
  return `${day}/${month}/${year}`
}

export const invalidateWorkforce = (queryClient, warehouse_id, families, filters = {}) => {
  queryClient.invalidateQueries({
    predicate: q => {
      if (q.queryKey?.[0] !== 'workforce') return false
      if (q.queryKey?.[1] !== warehouse_id) return false
      if (!families.includes(q.queryKey?.[2])) return false
      if (filters.shift_id && q.queryKey[2] === 'attendance' && q.queryKey[3] !== filters.shift_id) return false
      return true
    },
  })
}

export const CONTRACT_TYPE_BADGE_CLASS = {
  IPS: 'bg-blue-50 text-blue-700 border-blue-200',
  MP: 'bg-pink-50 text-pink-700 border-pink-200',
  ADECCO: 'bg-gray-50 text-gray-700 border-gray-200',
  CUBBO: 'bg-gray-50 text-gray-700 border-gray-200',
}

export const formatMoney = (value, currency) => {
  if (value == null || isNaN(value)) return '-'
  const n = Number(value)
  const formatted = n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency ? `${currency} ${formatted}` : formatted
}
